import { useFlag } from '@calendly/feature-check-react'
import { useMemo } from 'react'

import {
  ENT_IT_WORKFLOW_ACTION_EMAIL_SOMEONE_ELSE
} from 'constants/feature_flags'
import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell'
import { useNewEmptyStates } from 'features/empty_state'
import { isSmsAction } from 'services/workflows/actions'

import { publisherWorkflowsT } from '../publisher_workflows_i18n'
import templatesList from './templates.json'

// i18n-tasks-use t('publisher.workflows.workflow_templates.#{template_name}.name'
// i18n-tasks-use t('publisher.workflows.workflow_templates.#{template_name}.description'

/** No module-level memo: `isAppShellRebrand` can change mid-session; callers use `useMemo` where hot. */
const translatedTemplates = (isAppShellRebrand = false) => {
  const mapRow = (t) => ({
    ...t,
    name: publisherWorkflowsT(`publisher.workflows.workflow_templates.${t.i18n_prefix}.name`, isAppShellRebrand),
    description: publisherWorkflowsT(`publisher.workflows.workflow_templates.${t.i18n_prefix}.description`, isAppShellRebrand),
    workflow_rule: {
      ...t.workflow_rule,
      event_name: t.workflow_rule.event_name
    }
  })

  return templatesList.map(mapRow)
}

const UseNewEmptyState = () => useNewEmptyStates()

const POPULAR_TEMPLATES = [2, 1, 3, 9, 11, 6]
const EMPTY_STATE_POPULAR_TEMPLATES = [2, 10, 3]
const SEND_EMAIL_TO_SOMEONE_ELSE_TEMPLATES = [18, 19, 20]
export const NO_SHOW_TEMPLATE_ID = 21

export const SMS_TO_INVITEE_TEMPLATES = [12, 14, 13, 15, 17]
export const SMS_TO_HOST_TEMPLATES = [9, 10, 11]
const SMS_TEMPLATES = [...SMS_TO_INVITEE_TEMPLATES, ...SMS_TO_HOST_TEMPLATES]

export const getTemplatesForTiles = () => {
  if (UseNewEmptyState()) {
    return EMPTY_STATE_POPULAR_TEMPLATES
  }
  return POPULAR_TEMPLATES
}

export const useTemplateTiles = () => useMemo(() => POPULAR_TEMPLATES, [])

export const useWorkflowTemplatesList = (templateIds) => {
  const workflowActionEmailSomeoneElseEnabled = useFlag(ENT_IT_WORKFLOW_ACTION_EMAIL_SOMEONE_ELSE)
  const isAppShellRebrand = useProductRefreshAppShell()

  return useMemo(() => {
    let templates = translatedTemplates(isAppShellRebrand)
    templates.sort((a, b) => a.order - b.order)

    // Remove this method on CAL-72443
    if (!workflowActionEmailSomeoneElseEnabled) {
      templates = templates.filter(({ id }) => !SEND_EMAIL_TO_SOMEONE_ELSE_TEMPLATES.includes(id))
    }

    if (!templateIds) {
      return templates
    }

    return templateIds.map((templateId) => templates.find(({ id }) => templateId === id))
  }, [templateIds, workflowActionEmailSomeoneElseEnabled, isAppShellRebrand])
}

export const isSmsTemplate = (templateId) => SMS_TEMPLATES.includes(parseInt(templateId, 10))
export const shouldBlockSms = (isSmsDeactivated, templateId, workflowAction = '') => isSmsDeactivated
  && (isSmsAction(workflowAction) || isSmsTemplate(templateId))

export const isSmsToInvitee = (templateId, workflowAction = '') => workflowAction.includes('sms.event_to_invitee')
  || SMS_TO_INVITEE_TEMPLATES.includes(templateId)
export const isSmsToPublisher = (templateId, workflowAction = '') => workflowAction.includes('sms.event_to_publisher')
  || SMS_TO_HOST_TEMPLATES.includes(templateId)

export const shouldBlockTrialSMSForPublisher = (isTrial, smsPublisherPaywallFeatureFlagEnabled
) => isTrial && smsPublisherPaywallFeatureFlagEnabled

export const findWorkflowTemplate = (templateId, isAppShellRebrand = false) => (
  translatedTemplates(isAppShellRebrand).find(({ id }) => (id === templateId))
)
